<template>
  <common-modal-section class="mypage-change-paid-plan-modal-section" @close="handleCloseClicked">
    <div class="mypage-change-paid-plan-modal-section__header" slot="header">
      <p class="mypage-change-paid-plan-modal-section__title">
        {{ title }}
      </p>
    </div>
    <div class="mypage-change-paid-plan-modal-section__content" slot="default">
      <P class="mypage-change-paid-plan-modal-section__message" v-html="description" />
      <!-- 案内ページ未作成のため、「詳しくはこちら」を表示しない-->
      <atom-icon-link-button
        v-if="false"
        class="mypage-change-paid-plan-modal-section__link"
        :url="linkUrl"
        :linkText="$tc('MypagePage.PaidCompleteModal.linkLabel')"
        :colorType="'tertiary'"
        :imgPath="require('@/assets/img/icon/icon_external_link_gray.svg')"
      />
    </div>
    <div class="mypage-change-paid-plan-modal-section__button" slot="buttons">
      <P
        v-if="deviceNotSupportedMessage"
        class="mypage-change-paid-plan-modal-section__not-support-message"
        v-html="deviceNotSupportedMessage"
      />
      <atom-link-button
        :linkText="$tc('MypagePage.MypageTopPage.InAppPurchaseModal.buttonLabel')"
        :url="url"
        :colorType="'secondary'"
        @onClick="handleOkClicked"
      />
    </div>
  </common-modal-section>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import CommonModalSection from '@/components/common/modal/CommonModalSection.vue'
import AtomIconLinkButton from '@/components/atoms/AtomIconLinkButton.vue'
import AtomLinkButton from '@/components/atoms/AtomLinkButton.vue'
import { PurchasePaymentMethodType } from '@/store/stores/pageStore/common/ContractInfoStore'
import DeviceInfo from '@/util/DeviceInfo'
import I18n from '@/locales/I18n'
import Const from '@/util/Const'

/**
 * マイページ: アプリ内課金 支払いプランを切り替える/有料会員を解約するモーダルセクション
 */
export default defineComponent({
  name: 'MypageChangePaidPlanModalSection',
  components: { AtomLinkButton, AtomIconLinkButton, CommonModalSection },
  props: {
    /**
     * 有料プラン購入時の支払い方法
     */
    paymentMethod: {
      type: [String, null] as PropType<PurchasePaymentMethodType | null>,
      default: null,
    },
    /**
     * モーダルID
     */
    modalId: {
      type: String,
      default: '',
    },
    /**
     * タイトル
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * 説明
     */
    description: {
      type: String,
      default: '',
    },
    /**
     * URL
     */
    url: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const linkUrl = 'https://superformula.net/sf3/sfgo/'

    /**
     * 以下の場合に表示するメッセージ
     * App Storeで課金し、Apple製品以外でSFgoアプリを開いている
     */
    const deviceNotSupportedMessage = computed(() => {
      if (
        props.paymentMethod === 'AppStore' &&
        !(DeviceInfo.isiOS() || DeviceInfo.isAppleDeviceBrowser())
      ) {
        if (!(props.modalId === 'changePaidPlan' || props.modalId === 'cancelPaidPlan')) {
          // プランを変更、プランを解約の場合のみ表示を想定
          return ''
        }

        const lang = I18n.locale === 'ja' ? 'JA' : 'EN'

        return I18n.t(
          'MypagePage.MypageTopPage.InAppPurchaseModal.deviceNotSupported.description.android',
          {
            howToUrl:
              props.modalId === 'changePaidPlan'
                ? Const.EXTERNAL_LINKS.IN_APP_PURCHASE.CHANGE.IOS[lang]
                : Const.EXTERNAL_LINKS.IN_APP_PURCHASE.CANCEL.IOS[lang],
            contactUrl: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.SUPPORT.IOS,
          },
        ).toString()
      }
      // 上記以外
      return ''
    })

    const handleOkClicked = () => {
      emit('onClickOk')
    }
    const handleCloseClicked = () => {
      emit('close')
    }
    return {
      linkUrl,
      deviceNotSupportedMessage,
      handleOkClicked,
      handleCloseClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-change-paid-plan-modal-section {
  &__header {
    padding: 36px 16px 0;
  }

  &__title {
    font-size: 17px;
    font-weight: bold;
    color: $gray75;
    letter-spacing: 0.04em;
  }

  &__content {
    margin-top: 16px;
  }

  &__message {
    font-size: 13px;
    font-weight: bold;
    color: $gray75;
    letter-spacing: 0.04em;
  }

  &__link {
    ::v-deep {
      img {
        width: 20px;
        height: 20px;
      }
    }
  }

  &__not-support-message {
    font-size: 13px;
    font-weight: bold;
    color: $gray75;
    letter-spacing: 0.04em;
    margin-bottom: 16px;

    ::v-deep {
      a {
        text-decoration: underline;
      }
    }
  }

  &__button {
    width: 100%;
  }
}
</style>
